import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { IVisibleLogic } from 'config'
import { InputTypeOfLoans } from 'config/loan.input.fields.constants'
import { useEffect, useState } from 'react'
import { InputVisibleLogic, Modal } from 'stories/components'
import { confirm } from 'utils'
import { RenderInput } from 'utils/RenderInput'

export interface ITitleLogicRule {
  title: string
  visibleLogic: IVisibleLogic[][]
}

export interface ITitleVisibilityValue {
  title: string
  titleLogic: ITitleLogicRule[]
}

interface TitleVisibilityModalProps {
  isOpen: boolean
  originalTitle: string
  value: ITitleVisibilityValue
  fieldTitles: Record<string, { title: string; fields: Record<string, string> }>
  fieldInputs: InputTypeOfLoans
  onClose: () => void
  onOk: (value: ITitleVisibilityValue) => void
}

const newRule = (): ITitleLogicRule => ({
  title: '',
  visibleLogic: [],
})

export const TitleVisibilityModal = ({
  isOpen,
  originalTitle,
  value,
  fieldTitles,
  fieldInputs,
  onClose,
  onOk,
}: TitleVisibilityModalProps) => {
  const [title, setTitle] = useState('')
  const [rules, setRules] = useState<ITitleLogicRule[]>([])

  useEffect(() => {
    if (!isOpen) return
    setTitle(value.title || '')
    setRules(Array.isArray(value.titleLogic) ? cloneDeep(value.titleLogic) : [])
  }, [isOpen, value.title, value.titleLogic])

  const onAddRule = () => {
    setRules((prev) => [...prev, newRule()])
  }

  const onRemoveRule = async (ruleIndex: number) => {
    const ok = await confirm('Are you sure you want to remove this rule?')
    if (!ok) return
    setRules((prev) => prev.filter((_, index) => index !== ruleIndex))
  }

  const onChangeRuleTitle = (ruleIndex: number, ruleTitle: string) => {
    setRules((prev) => {
      const next = cloneDeep(prev)
      if (!next[ruleIndex]) return prev
      next[ruleIndex].title = ruleTitle
      return next
    })
  }

  const onChangeRuleLogic = (ruleIndex: number, logic: IVisibleLogic[][]) => {
    setRules((prev) => {
      const next = cloneDeep(prev)
      if (!next[ruleIndex]) return prev
      next[ruleIndex].visibleLogic = logic
      return next
    })
  }

  const handleOk = () => {
    onOk({
      title,
      titleLogic: rules.filter((rule) => rule.title || (rule.visibleLogic && rule.visibleLogic.length)),
    })
  }

  const renderRule = (rule: ITitleLogicRule, ruleIndex: number) => {
    const ruleKey = `title-rule-${ruleIndex}`
    return (
      <div className="border rounded-md p-3 mb-3 bg-white" key={ruleKey}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold">Rule {ruleIndex + 1}</p>
          <span className="btn-icon" onClick={() => onRemoveRule(ruleIndex)}>
            <TrashIcon className="w-4 h-4 text-red-500" />
          </span>
        </div>

        <div className="mb-3">
          <RenderInput
            Key={`${ruleKey}-title`}
            input={{
              inputType: 'text',
              title: 'Title (used when conditions below match)',
              value: rule.title,
              required: true,
            }}
            onChange={(_k: string, ruleTitle: string) => onChangeRuleTitle(ruleIndex, ruleTitle)}
          />
        </div>

        <InputVisibleLogic
          id={`${ruleKey}-logic`}
          title="Visible Conditions"
          value={rule.visibleLogic || []}
          fieldTitles={fieldTitles}
          fieldInputs={fieldInputs}
          options={{ hasNotVisible: true }}
          onChange={(logic: IVisibleLogic[][]) => onChangeRuleLogic(ruleIndex, logic)}
        />
      </div>
    )
  }

  if (!isOpen) return null

  return (
    <Modal
      isOpen
      title="Title Visibility Logic"
      titleClass="w-[640px]"
      titleOkay="OK"
      onClose={onClose}
      onOk={handleOk}
    >
      <div className="w-[640px]">
        <div className="mb-4">
          <RenderInput
            Key="title-default"
            input={{
              inputType: 'text',
              title: `Default Title (falls back to "${originalTitle}" when empty)`,
              value: title,
            }}
            onChange={(_k: string, nextTitle: string) => setTitle(nextTitle)}
          />
        </div>

        <div className="bg-slate-50 p-3 rounded-md">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold">
              Conditional Titles
              <span className="text-xs text-gray-500 ml-2 font-normal">
                The first rule whose conditions match wins. If no rule matches, the Default Title above is used.
              </span>
            </p>
            <span className="btn-icon" onClick={onAddRule}>
              <PlusIcon className="w-4 h-4" />
            </span>
          </div>

          {rules.length === 0 && (
            <p className="text-sm text-gray-500 italic">No conditional rules. Click the + above to add one.</p>
          )}

          {rules.map((rule, ruleIndex) => renderRule(rule, ruleIndex))}
        </div>
      </div>
    </Modal>
  )
}
